import React, { useState, useCallback } from 'react';
import { FileCheck } from 'lucide-react';
import { useMySurveys } from './hooks/useMySurveys';
import {
  PageHeader,
  StatsCards,
  TabSelector,
  Button,
  StatCardSkeleton,
  TableSkeleton,
} from '../../common';
import SurveyCard from './components/SurveyCard';
import ViewResponseModal from './components/ViewResponseModal';
import TakeSurveyModal from './take-survey/TakeSurveyModal';
import { MySurveyTab } from '../types';
import { MY_SURVEY_STATS_CARDS } from './utils';

const TABS: { k: MySurveyTab; label: string }[] = [
  { k: 'all', label: 'All' },
  { k: 'pending', label: 'Pending' },
  { k: 'inProgress', label: 'In Progress' },
  { k: 'completed', label: 'Completed' },
];

const SurveyListView: React.FC = () => {
  const {
    stats,
    surveys,
    activeTab,
    isLoadingStats,
    isLoadingSurveys,
    statsError,
    surveysError,
    setActiveTab,
    tabCounts,
    refreshData,
  } = useMySurveys();

  const [responseModalId, setResponseModalId] = useState<string | null>(null);
  const [takeSurveyId, setTakeSurveyId] = useState<string | null>(null);

  const handleViewResponse = useCallback((responseId: string) => {
    setResponseModalId(responseId);
  }, []);

  const handleCloseResponseModal = useCallback(() => {
    setResponseModalId(null);
  }, []);

  const handleOpenTakeSurvey = useCallback((surveyId: string) => {
    setTakeSurveyId(surveyId);
  }, []);

  const handleCloseTakeSurvey = useCallback(() => {
    setTakeSurveyId(null);
  }, []);

  const handleTakeSubmitted = useCallback(() => {
    setTakeSurveyId(null);
    refreshData();
  }, [refreshData]);

  const tabs = TABS.map((t) => ({ ...t, count: tabCounts[t.k] ?? 0 }));
  const hasError = statsError || surveysError;

  return (
    <div className="w-full">
      <PageHeader
        title="My Surveys"
        description="Surveys assigned to you · answer them and review what you have already submitted"
        breadcrumbs={[{ label: 'Home', path: '/dashboard' }, { label: 'My Surveys' }]}
      />

      {hasError ? (
        <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-card shadow-[var(--shadow-sm)] text-center px-6 py-14">
          <h3 className="text-heading-sm text-[var(--color-text-primary)] mb-1">Failed to load surveys</h3>
          <p className="text-body-sm text-[var(--color-text-secondary)] mb-5">
            Something went wrong while fetching your surveys. Please try again.
          </p>
          <Button appearance="primary" size="small" onClick={() => refreshData()}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          <div className="mb-5 sm:mb-6">
            {isLoadingStats ? (
              <StatCardSkeleton count={4} />
            ) : (
              <StatsCards stats={stats} cards={MY_SURVEY_STATS_CARDS} variant="simple-icon" valueOnTop />
            )}
          </div>

          <div className="mb-5 sm:mb-6">
            <TabSelector tabs={tabs} active={activeTab} onChange={setActiveTab} />
          </div>

          {isLoadingSurveys ? (
            <TableSkeleton rows={4} columns={3} />
          ) : surveys.length === 0 ? (
            <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-card shadow-[var(--shadow-sm)] flex flex-col items-center justify-center text-center px-6 py-12">
              <span className="w-16 h-16 rounded-[18px] grid place-items-center bg-[var(--color-bg-muted)] mb-1.5">
                <FileCheck className="w-[30px] h-[30px] text-[var(--color-layout-header-icon-secondary)]" />
              </span>
              <div className="text-[15px] font-bold text-[var(--color-text-primary)]">Nothing here</div>
              <div className="text-[13px] text-[var(--color-text-tertiary)]">
                {activeTab === 'all'
                  ? 'No surveys assigned to you.'
                  : `You have no ${TABS.find((t) => t.k === activeTab)?.label.toLowerCase()} surveys right now.`}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
              {surveys.map((survey) => (
                <SurveyCard
                  key={survey._id}
                  survey={survey}
                  onViewInfo={handleOpenTakeSurvey}
                  onViewResponse={handleViewResponse}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* View Response Modal */}
      <ViewResponseModal
        surveyId={responseModalId || ''}
        isOpen={!!responseModalId}
        onClose={handleCloseResponseModal}
      />

      {/* Take Survey Modal (Figma flow) */}
      {takeSurveyId && (
        <TakeSurveyModal
          surveyId={takeSurveyId}
          onClose={handleCloseTakeSurvey}
          onSubmitted={handleTakeSubmitted}
        />
      )}
    </div>
  );
};

const MySurveys: React.FC = () => {
  return <SurveyListView />;
};

export default MySurveys;
